// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectCollectingRouteSites } from 'redux/slices/collectingRoute';
import { Path } from 'routes';
import { CollectingRouteSession } from 'util/collectingRoute';
import { getApiPath } from 'util/commons';
import { StorageKeys } from 'util/Enums';
import { useCheckUserActivity } from './useCheckUserActivity';

export const useCheckRouteCollectProcess = () => {
  const { routeId, siteId } = useParams<{
    routeId?: string;
    siteId?: string;
  }>();
  const collectingRouteSites = useSelector(selectCollectingRouteSites);
  const [validCollectSiteIds, setValidCollectSiteIds] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string>();
  const [processedSites, setProcessedSites] = useState<string[]>([]);
  const history = useHistory();
  const [isInitialized, setIsInitialized] = useState(false);
  const isMapView = useMemo(
    () =>
      localStorage.getItem(StorageKeys.COLLECT_MAP_VIEW_MODE_STORAGE_KEY) ===
      'map',
    []
  );
  const isNoneRouteId = useMemo(() => routeId === 'none', [routeId]);
  const { t } = useTranslation();

  const checkRouteSessionExpired = useCallback(() => {
    if (!collectingRouteSites || !routeId || isNoneRouteId) {
      return;
    }

    const collectSession = CollectingRouteSession.get(routeId, false);

    if (
      collectSession &&
      CollectingRouteSession.checkSessionExpired(collectSession)
    ) {
      CollectingRouteSession.remove(routeId);
      toast.info(t('messages.M_route_collect_expired'));

      history.replace(`${Path.routeList}${window.location.search}`);
    }
  }, [collectingRouteSites, routeId, isNoneRouteId, t, history]);

  // Check session when user comes back to the tab or window
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkRouteSessionExpired();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isNoneRouteId, routeId, collectingRouteSites]);

  // Check user activity to determine if the session has expired
  useCheckUserActivity({
    timeout: 30 * 60, // 30 minutes
    onInactiveInterval: checkRouteSessionExpired,
    inactiveIntervalInSeconds: 30, // Check every 30 seconds when user is inactive
  });

  useEffect(() => {
    if (!collectingRouteSites || !routeId || isMapView || isNoneRouteId) {
      return;
    }

    setIsInitialized(false);
    const collectingSession = CollectingRouteSession.get(routeId);

    const savedMap = collectingSession?.siteMap || {};

    const viewableSitesMap = Object.fromEntries(
      collectingRouteSites.sites.map((site) => [
        site.id,
        savedMap[site.id] ?? false,
      ])
    );

    const viewableSites = collectingRouteSites.sites.filter(
      (site) => viewableSitesMap[site.id]
    );
    const viewableSiteIds = viewableSites.map((site) => site.id);

    if (!Object.values(viewableSitesMap).some((value) => value)) {
      history.replace(
        `${getApiPath(Path.routeDetail, { routeId })}${window.location.search}`
      );

      return;
    }

    CollectingRouteSession.save({
      routeId,
      siteMap: viewableSitesMap,
    });

    setValidCollectSiteIds(
      Object.keys(viewableSitesMap).filter((id) => viewableSitesMap[id])
    );

    const savedProcessedIds = collectingSession?.processedSiteIds || [];

    const { available, nonExisted } = savedProcessedIds.reduce<{
      available: string[];
      nonExisted: string[];
    }>(
      (acc, id) => {
        if (Object.keys(viewableSitesMap).includes(id)) {
          acc.available.push(id);
        } else {
          acc.nonExisted.push(id);
        }

        return acc;
      },
      {
        available: [],
        nonExisted: [],
      }
    );

    if (nonExisted.length) {
      CollectingRouteSession.removeProcessedSites({
        routeId,
        siteIds: nonExisted,
      });
    }

    const unProcessedSiteIds = viewableSites
      .filter(
        (site) =>
          !available.includes(site.id) &&
          (site.enableSubstituteRegist ||
            (site.weight !== '0' &&
              (site.collectRequestFlg || site.emissionFlg)))
      )
      .map((site) => site.id);

    setProcessedSites(available);

    if (!unProcessedSiteIds.length) {
      setIsInitialized(true);

      return;
    }

    if (available.length) {
      const lastProcessedId = available[available.length - 1];
      const lastProcessedIndex = viewableSiteIds.indexOf(lastProcessedId);

      const nextValidSiteId = unProcessedSiteIds.find((id) => {
        return viewableSiteIds.indexOf(id) > lastProcessedIndex;
      });

      if (nextValidSiteId) {
        setActiveId(nextValidSiteId);
      } else {
        setActiveId(unProcessedSiteIds[0]);
      }
    } else {
      setActiveId(unProcessedSiteIds[0]);
    }

    setIsInitialized(true);
  }, [collectingRouteSites, routeId, isMapView, isNoneRouteId]);

  const displayingSites = useMemo(() => {
    if (
      !collectingRouteSites?.sites.length ||
      !validCollectSiteIds.length ||
      isMapView ||
      isNoneRouteId
    ) {
      return [];
    }

    return collectingRouteSites.sites.filter((site) =>
      validCollectSiteIds.includes(site.id)
    );
  }, [
    validCollectSiteIds,
    collectingRouteSites?.sites,
    isMapView,
    isNoneRouteId,
  ]);

  useEffect(() => {
    if (!siteId || !routeId || !isInitialized || isMapView || isNoneRouteId) {
      return;
    }

    const canBeCollected = displayingSites.some((site) => site.id === siteId);

    if (!canBeCollected) {
      history.replace(
        `${getApiPath(Path.routeSiteSelection, { routeId })}${
          window.location.search
        }`
      );
    }
  }, [
    siteId,
    displayingSites,
    routeId,
    isInitialized,
    isMapView,
    isNoneRouteId,
  ]);

  return {
    validCollectSiteIds,
    activeId,
    processedSites,
    displayingSites: displayingSites ?? [],
  };
};
